import React, { useState } from "react";
import { Form } from "react-bootstrap";
import BaseInfoIcon from "../../../../../icons/base-info.icon";
import DropdownSelector from "../../../../../Common/dropdown-selector";
import NumberInputWithControls from "../../../../../Common/number-input-with-controls/number-input-with-controls.component";
import styles from "./module.row.module.scss";

interface ModuleRowProps {
  label: string;
  unitLabel: string;
  isChecked?: boolean;
  userCount?: number;
  limitType?: "Limited" | "Unlimited";
  onChange?: (data: {
    label: string;
    isChecked: boolean;
    userCount: number;
    limitType: "Limited" | "Unlimited";
  }) => void;
}

const ModuleRow = ({
  label,
  unitLabel,
  isChecked: initialChecked = true,
  userCount: initialUserCount = 10,
  limitType: initialLimitType = "Limited",
  onChange,
}: ModuleRowProps) => {
  const [isChecked, setIsChecked] = useState<boolean>(initialChecked);
  const [userCount, setUserCount] = useState<number>(initialUserCount);
  const [selectedOption, setSelectedOption] = useState<string>(initialLimitType);

  const userLimitOptions = [
    { label: "Limited", value: "Limited" },
    { label: "Unlimited", value: "Unlimited" },
  ];

  const selectedLimitOption = userLimitOptions.find(
    (option) => option.value === selectedOption
  ) || userLimitOptions[0];

  const handleIncrement = () => {
    setUserCount((prev) => {
      const newValue = prev + 1;
      onChange?.({
        label,
        isChecked,
        userCount: newValue,
        limitType: selectedOption as "Limited" | "Unlimited",
      });
      return newValue;
    });
  };

  const handleDecrement = () => {
    setUserCount((prev) => {
      const newValue = Math.max(0, prev - 1);
      onChange?.({
        label,
        isChecked,
        userCount: newValue,
        limitType: selectedOption as "Limited" | "Unlimited",
      });
      return newValue;
    });
  };

  const handleInputChange = (value: string | number) => {
    const numValue = typeof value === "string" ? Number.parseInt(value, 10) : value;
    if (!isNaN(numValue) && numValue >= 0) {
      setUserCount(numValue);
      onChange?.({
        label,
        isChecked,
        userCount: numValue,
        limitType: selectedOption as "Limited" | "Unlimited",
      });
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    setIsChecked(checked);
    onChange?.({
      label,
      isChecked: checked,
      userCount,
      limitType: selectedOption as "Limited" | "Unlimited",
    });
  };

  const handleLimitTypeChange = (option: { label: string; value: string }) => {
    setSelectedOption(option.value);
    onChange?.({
      label,
      isChecked,
      userCount,
      limitType: option.value as "Limited" | "Unlimited",
    });
  };

  return (
    <section
      className={styles.row}
      aria-label={`${label} Module Configuration`}
    >
      <div className={styles.labelContainer}>
        <div className={styles.labelWrapper}>
          <Form.Group className="fixed-checkbox blue-checkbox fix-checkbox pointer-checkbox">
            <Form.Check
              type="checkbox"
              checked={isChecked}
              onChange={(e) => handleCheckboxChange(e.target.checked)}
              id={`module-${label.toLowerCase().replace(/\s+/g, "-")}-checkbox`}
              className="checkbox"
            />
          </Form.Group>

          <label
            htmlFor={`module-${label.toLowerCase().replace(/\s+/g, "-")}-checkbox`}
            className={styles.label}
          >
            {label}
          </label>
        </div>

        <BaseInfoIcon />
      </div>

      {isChecked && (
        <div className={styles.optionsContainer}>
          <DropdownSelector
            list={userLimitOptions}
            selected={selectedLimitOption}
            onItemSelected={handleLimitTypeChange}
            toggleClassName={styles.selectButton}
            className={styles.selectWrapper}
            popperConfig={{ strategy: "fixed" }}
          >
            <span className={styles.selectText}>
              {selectedOption}
            </span>
            <div className="arrow-icon" />
          </DropdownSelector>

          <div className={styles.inputContainer}>
            <NumberInputWithControls
              id="user-count-input"
              value={userCount}
              onChange={handleInputChange}
              onIncrement={handleIncrement}
              onDecrement={handleDecrement}
              min="0"
              ariaLabel="User count"
              wrapperClassName={styles.inputWrapper}
            />
            <span className={styles.userLabel}>
              {unitLabel}
            </span>
          </div>
        </div>
      )}
    </section>
  );
};

export default ModuleRow;